import { supabase } from './supabase';

/**
 * Service for handling email change verification
 */
const emailChangeService = {
  /**
   * Initiate email change verification process
   * @param {string} userId - User ID
   * @param {string} currentEmail - Current email address
   * @param {string} newEmail - New email address to change to
   * @param {string} redirectUrl - Optional URL to redirect after verification
   * @returns {Promise<Object>} - Result of the operation
   */
  initiateEmailChange: async (userId, currentEmail, newEmail, redirectUrl = null) => {
    try {
      if (!userId || !currentEmail || !newEmail) {
        return {
          success: false,
          error: 'User ID, current email, and new email are required'
        };
      }

      // Check if new email is different from current email
      if (currentEmail.toLowerCase().trim() === newEmail.toLowerCase().trim()) {
        return {
          success: false,
          error: 'New email must be different from current email'
        };
      }

      console.log('Calling verify-email-change with:', { userId, currentEmail, newEmail });

      // Get the Supabase URL and service key from the client
      const supabaseUrl = supabase.supabaseUrl;
      const serviceKey = supabase.supabaseKey;

      // Use direct fetch to properly handle all HTTP status codes
      const response = await fetch(`${supabaseUrl}/functions/v1/verify-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${serviceKey}`,
        },
        body: JSON.stringify({
          userId,
          currentEmail,
          newEmail,
          redirectUrl: redirectUrl || `${window.location.origin}/auth/verify-email-change`
        })
      });

      const data = await response.json();
      console.log('verify-email-change response:', { status: response.status, data });

      // Handle non-2xx responses
      if (!response.ok) {
        const errorMsg = data?.error || `Request failed with status ${response.status}`;
        console.error('Email change returned error:', errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      }

      // Handle response body that indicates failure
      if (data && !data.success) {
        const errorMsg = data.error || 'Unknown error during email change verification';
        console.error('Email change service error:', errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      }

      if (!data) {
        return {
          success: false,
          error: 'No response from email change service'
        };
      }

      console.log('Email change successful');
      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Verification email sent successfully. Please check your new email address to complete the change.'
      };
    } catch (error) {
      console.error('Error in initiateEmailChange:', error);
      return {
        success: false,
        error: error.message || 'Failed to initiate email change verification'
      };
    }
  },

  /**
   * Confirm email change with verification token
   * @param {string} token - Verification token
   * @param {string} email - New email address
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Result of the operation
   */
  confirmEmailChange: async (token, email, userId) => {
    try {
      if (!token || !email || !userId) {
        return {
          success: false,
          error: 'Token, email, and user ID are required'
        };
      }

      // Get the Supabase URL and anonymous key from the client
      const supabaseUrl = supabase.supabaseUrl;
      const anonKey = supabase.supabaseKey;
      
      // Use direct fetch instead of supabase.functions.invoke() to avoid JWT requirements
      // This is a public verification endpoint that uses the anonymous key
      const response = await fetch(`${supabaseUrl}/functions/v1/confirm-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({
          token,
          email,
          userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Error confirming email change:', data);
        return {
          success: false,
          error: data.error || 'Failed to confirm email change'
        };
      }

      if (!data || !data.success) {
        const errorMsg = data?.error || 'Unknown error during email change confirmation';
        console.error('Email change confirmation error:', errorMsg);
        return {
          success: false,
          error: errorMsg
        };
      }

      return {
        success: true,
        data: data.data || data,
        message: 'Email address successfully updated'
      };
    } catch (error) {
      console.error('Error in confirmEmailChange:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm email change'
      };
    }
  },

  /**
   * Check if an email change is pending for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Result of the operation with pending change details
   */
  checkPendingEmailChange: async (userId) => {
    try {
      if (!userId) {
        return {
          success: false,
          error: 'User ID is required'
        };
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .select('new_email, email_change_token_expires')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error checking pending email change:', error);
        return {
          success: false,
          error: error.message
        };
      }

      // Check if there's a pending email change
      if (data && data.new_email && data.email_change_token_expires) {
        const expiryTime = new Date(data.email_change_token_expires);
        const now = new Date();

        if (expiryTime > now) {
          return {
            success: true,
            pending: true,
            newEmail: data.new_email,
            expiresAt: data.email_change_token_expires
          };
        } else {
          // Token expired, clear the pending change
          await supabase
            .from('user_profiles')
            .update({
              new_email: null,
              email_change_token: null,
              email_change_token_expires: null
            })
            .eq('id', userId);

          return {
            success: true,
            pending: false
          };
        }
      }

      return {
        success: true,
        pending: false
      };
    } catch (error) {
      console.error('Error in checkPendingEmailChange:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  /**
   * Cancel a pending email change
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Result of the operation
   */
  cancelEmailChange: async (userId) => {
    try {
      if (!userId) {
        return {
          success: false,
          error: 'User ID is required'
        };
      }

      const { error } = await supabase
        .from('user_profiles')
        .update({
          new_email: null,
          email_change_token: null,
          email_change_token_expires: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        console.error('Error canceling email change:', error);
        return {
          success: false,
          error: error.message
        };
      }

      return {
        success: true,
        message: 'Pending email change canceled successfully'
      };
    } catch (error) {
      console.error('Error in cancelEmailChange:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default emailChangeService;
